import { readArrowLabel } from './arrowTerminalLabels.js';
import { aboutKindForType } from './textAboutObject.js';
import { membershipIdFromFrameShape } from './frameMembership.js';

function digestRefForShape(shape) {
  if (!shape) return '';
  if (shape.type === 'pin-card') {
    return String(shape.meta?.activityId || shape.id || '').trim();
  }
  if (shape.type === 'frame') {
    return String(membershipIdFromFrameShape(shape) || shape.id || '').trim();
  }
  return String(shape.id || '').trim();
}

function displayLabelForShape(shape) {
  if (!shape) return '';
  if (shape.type === 'pin-card') {
    return String(shape.props?.title || 'Pin').trim();
  }
  if (shape.type === 'frame') {
    return String(shape.props?.name || shape.props?.label || 'Frame').trim();
  }
  if (shape.type === 'suggest-pin' || shape.type === 'conversation-pin') {
    return String(shape.props?.title || shape.type).trim();
  }
  return String(shape.props?.name || shape.props?.text || shape.type || '').trim();
}

/**
 * Labeled + both-ends-bound connecting line → digestable relation claim.
 * Unlabeled or unbound lines are live structure only (not ledger).
 */
export function readArrowRelation(editor, shape) {
  if (!editor || !shape || shape.type !== 'arrow') return null;
  const relationLabel = readArrowLabel(shape);
  if (!relationLabel) return null;

  const bindings = editor.getBindingsFromShape?.(shape.id, 'arrow') || [];
  let start = null;
  let end = null;
  for (const binding of bindings) {
    const target = editor.getShape?.(binding?.toId);
    if (!target) continue;
    if (binding?.props?.terminal === 'start') start = target;
    if (binding?.props?.terminal === 'end') end = target;
  }
  if (!start || !end) return null;

  const fromId = digestRefForShape(start);
  const toId = digestRefForShape(end);
  if (!fromId || !toId) return null;

  const fromKind = aboutKindForType(start.type);
  const toKind = aboutKindForType(end.type);
  const fromLabel = displayLabelForShape(start);
  const toLabel = displayLabelForShape(end);
  const aboutPinId = fromKind === 'pin' ? fromId : toKind === 'pin' ? toId : '';
  const aboutShapeId = fromKind === 'frame' ? fromId : toKind === 'frame' ? toId : '';
  const aboutKind = fromKind === 'frame' || toKind === 'frame'
    ? 'frame'
    : fromKind === 'pin' || toKind === 'pin'
      ? 'pin'
      : fromKind;

  return {
    shapeId: String(shape.id),
    relationLabel,
    fromId,
    toId,
    fromKind,
    toKind,
    fromLabel,
    toLabel,
    aboutPinId,
    aboutShapeId,
    aboutKind,
    intentSummary: `${fromLabel || fromId} → ${toLabel || toId}: ${relationLabel}`,
  };
}

/** Identity of a digestable line. Empty when the line is unlabeled or unbound. */
export function arrowRelationKey(relation) {
  if (!relation?.fromId || !relation?.toId || !relation?.relationLabel) return '';
  return `${relation.fromId}\0${relation.toId}\0${relation.relationLabel}`;
}

/**
 * What to do with a connecting line.
 * Lines already on the board (`baseline`) are remembered and not posted.
 * A later edit posts only when that identity changes.
 * @returns {'remember' | 'forget' | 'enqueue' | 'revoke' | 'ignore'}
 */
export function arrowRelationDigestDecision(prevKey, nextKey, { baseline = false } = {}) {
  const prev = String(prevKey || '');
  const next = String(nextKey || '');
  if (baseline) return next ? 'remember' : 'forget';
  if (prev === next) return 'ignore';
  if (!next) return prev ? 'revoke' : 'ignore';
  return 'enqueue';
}

export function buildArrowRelationAddEvent(editor, shape, at = Date.now()) {
  const relation = readArrowRelation(editor, shape);
  if (!relation) return null;
  return {
    op: 'add',
    shapeId: relation.shapeId,
    label: relation.relationLabel,
    intentSummary: relation.intentSummary,
    ...(relation.aboutPinId ? { aboutPinId: relation.aboutPinId } : {}),
    ...(relation.aboutShapeId ? { aboutShapeId: relation.aboutShapeId } : {}),
    ...(relation.aboutKind ? { aboutKind: relation.aboutKind } : {}),
    fromId: relation.fromId,
    toId: relation.toId,
    fromKind: relation.fromKind,
    toKind: relation.toKind,
    ...(relation.fromLabel ? { fromLabel: relation.fromLabel.slice(0, 80) } : {}),
    ...(relation.toLabel ? { toLabel: relation.toLabel.slice(0, 80) } : {}),
    relationLabel: relation.relationLabel,
    source: 'connecting-line',
    at,
  };
}
